import * as React from 'react'
import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi, setToken } from '@/lib/api'
import { Globe, Lock, Phone, Loader2 } from 'lucide-react'
import { supportedLanguages } from '@/i18n/config'

export function LoginPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Redirect if already logged in
  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') === 'true') {
      navigate({ to: '/dashboard' })
    }
  }, [navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!phone || !password) {
      setError(t('login.invalidCredentials'))
      return
    }
    setLoading(true)
    setError('')
    try {
      const res: any = await authApi.login({ telephone: phone, password })
      if (res?.code === 1 || res?.code === 200 || res?.success || res?.data) {
        sessionStorage.setItem('isLoggedIn', 'true')
        const userName = res?.data?.username || res?.username || phone
        const displayName = res?.data?.display_name || userName
        const role = res?.data?.role || 'admin'
        sessionStorage.setItem('userName', displayName)
        sessionStorage.setItem('userRole', role)
        if (res?.data?.token) {
          setToken(res.data.token)
        }
        navigate({ to: '/dashboard' })
      } else {
        setError(res?.msg || res?.message || t('login.invalidCredentials'))
      }
    } catch (err: any) {
      setError(err?.response?.data?.msg || t('login.invalidCredentials'))
    } finally {
      setLoading(false)
    }
  }

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang)
    localStorage.setItem('lang', lang)
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-40 -top-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
      </div>

      {/* Language switcher top-right */}
      <div className="absolute right-6 top-6 z-10 flex gap-1.5">
        {supportedLanguages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs transition-all hover:bg-white/40 ${
              i18n.language === lang.code ? 'bg-white/60 font-semibold shadow-sm' : 'text-muted-foreground'
            }`}
            title={lang.name}
          >
            <span className="text-base">{lang.flag}</span>
          </button>
        ))}
      </div>

      <Card className="relative z-10 w-[420px] shadow-2xl border-0">
        <CardHeader className="space-y-3 text-center pb-2">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-primary/70 shadow-lg">
            <Globe className="h-8 w-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold">{t('login.title')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('login.subtitle')}</p>
        </CardHeader>
        <CardContent className="pt-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">{t('login.phone')}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder={t('login.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('login.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            {error && (
              <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full h-11" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('login.signingIn')}
                </>
              ) : (
                t('login.signIn')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
